import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setVTOResult,
  selectCurrentVTOSession
} from '../redux/slices/vtoSlice';
import { setActiveItems } from '../redux/slices/clothingItemsSlice';
import { selectPersonImageDisplayProps } from '../redux/selectors/personImageSelectors';
import { useGetUserClothingQuery } from '../redux/apis/endpoints/closetApi';

/**
 * Hook to integrate existing VTO system with new Redux state
 */
export const useVTOIntegration = (virtualTryOnResults, clothingItems = []) => {
  const dispatch = useDispatch();
  const personImageProps = useSelector(selectPersonImageDisplayProps);
  const currentSession = useSelector(selectCurrentVTOSession);

  // Get all user clothing items for looking up by ID (same as FitHistorySection)
  const { data: allClothingItems = [] } = useGetUserClothingQuery();

  // Monitor VTO results and update Redux state
  useEffect(() => {
    if (virtualTryOnResults && virtualTryOnResults.length > 0) {
      const latestResult = virtualTryOnResults[virtualTryOnResults.length - 1];

      const resultImageUrl = Array.isArray(latestResult.resultImage) ? latestResult.resultImage[0]?.url || latestResult.resultImage[0]?.id : latestResult.resultImage;
      if (resultImageUrl) {
        // Find the actual clothing item from the clothing items list
        // Use loose equality to handle string/number ID mismatches
        const clothingItem = clothingItems.find(item => item.id == latestResult.garmentId);

        console.log('[VTOInteg] VTO Integration - Item Lookup:', {
          garmentId: latestResult.garmentId,
          foundItem: !!clothingItem,
          metadataImage: latestResult.metadata?.garmentImage
        });

        const itemToUse = clothingItem || {
          id: latestResult.garmentId,
          // Use garmentImage from metadata if available, otherwise fallback
          image: latestResult.metadata?.garmentImage || '',
          imageUrl: latestResult.metadata?.garmentImage || '', // Add imageUrl for compatibility
          name: latestResult.metadata?.garmentName || 'Unknown item',
          clothesName: latestResult.metadata?.garmentName || 'Unknown item',
          category: latestResult.metadata?.garmentType || 'clothing'
        };

        // Update VTO Redux state with the result
        dispatch(setVTOResult({
          resultImageId: resultImageUrl, // Store as ID/URL reference
          metadata: {
            garmentType: itemToUse.category || 'clothing',
            garmentName: itemToUse.clothesName,
            garmentImage: itemToUse.image
          }
        }));

        // Set active items for "Currently Wearing" section
        // Use layeredItems from metadata if available (multi-item support)
        // Otherwise fall back to single item
        const layeredItems = latestResult.metadata?.layeredItems;
        if (layeredItems && Array.isArray(layeredItems) && layeredItems.length > 0) {
          console.log('[VTOInteg] VTO Integration - Using layeredItems from metadata:', {
            count: layeredItems.length,
            items: layeredItems.map(i => ({ id: i.id, name: i.name, hasImage: !!(i.image || i.imageUrl) }))
          });

          // Look up full clothing item data from cache (same as FitHistorySection)
          const allItems = layeredItems
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(metadataItem => {
              // Look up the full clothing item data from the cache using the ID
              const fullItem = allClothingItems.find(item => item.id === metadataItem.id);

              if (fullItem) {
                // Use the full item data from cache (includes image)
                console.log('[VTOInteg] Found clothing item in cache:', {
                  id: fullItem.id,
                  name: fullItem.clothesName,
                  hasImage: !!fullItem.image
                });
                return {
                  id: fullItem.id,
                  name: fullItem.clothesName,
                  clothesName: fullItem.clothesName,
                  category: metadataItem.category || fullItem.category,
                  image: fullItem.image,
                  imageUrl: fullItem.image?.url
                };
              } else {
                // Fallback: use metadata (might have image from backend enrichment)
                console.warn('[VTOInteg] Clothing item not found in cache, using metadata:', metadataItem.id);
                return {
                  id: metadataItem.id,
                  name: metadataItem.name || metadataItem.clothesName || 'Unknown Item',
                  clothesName: metadataItem.name || metadataItem.clothesName || 'Unknown Item',
                  category: metadataItem.category || metadataItem.garmentType,
                  image: metadataItem.imageUrl || metadataItem.image,
                  imageUrl: metadataItem.imageUrl || metadataItem.image
                };
              }
            })
            .filter(item => item != null);

          console.log('[VTOInteg] VTO Integration - Mapped items:', allItems.map(i => ({ id: i.id, hasImage: !!i.image })));
          dispatch(setActiveItems(allItems));
        } else {
          console.log('[VTOInteg] VTO Integration - Using single item (no layeredItems)');
          dispatch(setActiveItems([itemToUse]));
        }
      }
    }
  }, [virtualTryOnResults, clothingItems, dispatch]);

  return {
    // Could add additional integration utilities here
  };
};

export default useVTOIntegration;